import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, makeStyles } from '@material-ui/core';
import RankingSelectedMonth from '../../components/Ranking/RankingSelectedMonth';
import RankingList from '../../components/Ranking/RankingList';
import { rankingListItem, RankingModel } from '../../models/RankingModel';
import { User, UserModel } from '../../models/UserModel';

export default function RankingScreen() {
  const classes = useStyles();

  /************************************************
   * useState
   * *********************************************/

  /** ログインユーザー情報 **/
  const [loginUserInfo, setLoginUserInfo] = useState<User | null>(null);

  /** 選択月 */
  const [selectedDate, setSelectedDate] = useState(new Date());
  /** 表示列 */
  const [selectedColumn, setSelectedColumn] = useState('receipt');
  /** 当月フラグ */
  // const [parentIsThisMonth, setParentIsThisMonth] = useState(true);

  /** ランキングリスト **/
  const [rankingLists, setRankingLists] = useState<rankingListItem[]>([]);
  /** ランキングリスト総数 */
  const [rankingItemCount, setRankingItemCount] = useState<number | null>(null);

  /** ページング用 **/
  /** 現在のページ */
  const [currentPage, setCurrentPage] = useState(1);
  /** 最後のページ */
  const [lastPage, setLastPage] = useState<number | null>(null);

  /** ローディング */
  const [isLoading, setIsLoading] = useState(true);

  /** スクロール検知用 */
  const observerRef = useRef<HTMLTableRowElement | null>(null);

  /**
   * リクエスト世代管理
   * - 月・列の切替やスクロールで複数の取得が並行した際、
   *   遅れて届いた「古い」応答を破棄して二重追記（足されていく現象）を防ぐ
   */
  const requestIdRef = useRef(0);

  /************************************************
   * データ取得関数
   * *********************************************/

  /** ログインユーザー情報を取得 **/
  const fetchUser = async () => {
    try {
      const userInfo = await UserModel.fetchMyUserInfo();
      setLoginUserInfo(userInfo);
    } catch (error) {
      console.error('ログインユーザー情報の取得に失敗:', error);
    }
  };

  /**
   * ランキングリストを取得
   * - 初期取得数は15件
   * - スクロールで追加取得（15件ずつ）
   * - ランキングリストがあるだけ追加で取得する
   */
  const fetchRankingData = async (page: number) => {
    /** この取得の世代番号（呼び出すたびに進める） */
    const reqId = ++requestIdRef.current;
    setIsLoading(true);
    try {
      /** 開始日 */
      const startDate = selectedDate.toISOString().slice(0, 7) + '-01';
      /** 終了日 */
      const endDate = `${selectedDate.getFullYear()}-${('0' + (selectedDate.getMonth() + 1)).slice(
        -2
      )}-${(
        '0' + new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0).getDate()
      ).slice(-2)}`;

      /** データの取得 */
      const api = await RankingModel.get_ranking_data({
        startDate,
        endDate,
        displayColumn: selectedColumn,
        // isThisMonth: parentIsThisMonth,
        page: page,
      });

      /** 古い応答は破棄（月・列の切替後に届いた前回分を無視する） */
      if (reqId !== requestIdRef.current) return;

      /** 最後のページをセット */
      setLastPage(api.lastPage ?? null);

      /** データの総件数をセット */
      setRankingItemCount(api.total ?? null);

      /**
       * 取得データをセット
       * - 1ページ目は置換（前回の月のデータを引き継がない）
       * - 2ページ目以降はスクロール追加読み込みのため追記
       */
      setRankingLists(prev => (page === 1 ? api.rankingList : [...prev, ...api.rankingList]));
    } catch (error) {
      console.error('ランキングデータの取得に失敗:', error);
    } finally {
      /** 最新の取得のみローディング状態を解除する */
      if (reqId === requestIdRef.current) setIsLoading(false);
    }
  };

  /** 初期データ取得 **/
  useEffect(() => {
    fetchUser();
  }, []);

  /** 表示月・列変更時にデータリセット */
  useEffect(() => {
    setRankingLists([]);
    setCurrentPage(1);
    /** 明示的に「1」ページ目を取得 ⇒ currentPageがすぐ変更されないため（stateが非同期） */
    fetchRankingData(1);
  }, [selectedDate, selectedColumn]);

  /** `currentPage` が更新されたら追加データを取得 */
  useEffect(() => {
    if (currentPage > 1) fetchRankingData(currentPage);
  }, [currentPage]);

  /** スクロール監視（最下部に達したら `currentPage` 更新） */
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (
        entries[0].isIntersecting &&
        !isLoading &&
        (lastPage === null || currentPage < lastPage)
      ) {
        setCurrentPage(prev => prev + 1);
      }
    },
    [isLoading, lastPage, currentPage]
  );

  /** スクロール監視のセットアップ */
  useEffect(() => {
    if (!observerRef.current || isLoading) return;
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: '0px',
      threshold: 0.5,
    });
    observer.observe(observerRef.current);
    return () => observer.disconnect();
  }, [handleObserver]);

  return (
    <>
      {/* 表示年月ボタン */}
      <RankingSelectedMonth
        parentCurrentDate={selectedDate}
        parentSetCurrentDate={setSelectedDate}
        // parentIsThisMonth={setParentIsThisMonth}
      />
      <Box
        className={rankingLists.length <= 15 ? classes.gridWrapper_limited : classes.gridWrapper}
      >
        {/* ランキング表 */}
        <RankingList
          rankList={rankingLists}
          observerRef={observerRef}
          parentDisplayColumn={selectedColumn}
          parentSetDisplayColumn={setSelectedColumn}
          rankingItemCount={rankingItemCount}
          isLoading={isLoading}
          loginUserInfo={loginUserInfo}
        />
      </Box>

      {/* ローディングインジケーター */}
      {isLoading && (
        <Box className={classes.loading}>
          <CircularProgress />
        </Box>
      )}
    </>
  );
}

/************************************************
 * style
 * *********************************************/

const useStyles = makeStyles({
  gridWrapper: {
    width: '95%',
    maxWidth: '1000px',
    marginInline: 'auto',
    height: '85%',
    overflow: 'hidden',
  },
  gridWrapper_limited: {
    width: '95%',
    maxWidth: '1000px',
    marginInline: 'auto',
    height: 'fit-content',
    overflow: 'hidden',
  },
  loading: {
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 9999,
  },
});
